import React from 'react';
import { AlertCircle, ExternalLink, Key, XCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../common/ui/dialog';
import { Button } from '../../common/ui/button';

const ApiKeyValidationModal = ({ isOpen, onClose, validationErrors, onGoToSettings }) => {
  const getProviderSteps = provider => {
    const steps = {
      openai: [
        'Go to OpenAI website (https://platform.openai.com)',
        'Sign in to your OpenAI account',
        'Navigate to API Keys section',
        'Click "Create new secret key"',
        'Copy the generated API key',
        'Paste it in the API Keys section below',
      ],
      deepgram: [
        'Go to Deepgram website (https://deepgram.com)',
        'Sign in to your Deepgram account',
        'Navigate to API Keys section',
        'Click "Create API Key"',
        'Copy the generated API key',
        'Paste it in the API Keys section below',
      ],
      fireworks: [
        'Go to Fireworks AI website (https://fireworks.ai)',
        'Sign in to your Fireworks account',
        'Navigate to API Keys section',
        'Click "Create API Key"',
        'Copy the generated API key',
        'Paste it in the API Keys section below',
      ],
      elevenlabs: [
        'Go to ElevenLabs website (https://elevenlabs.io)',
        'Sign in to your ElevenLabs account',
        'Navigate to API Keys section',
        'Click "Create API Key"',
        'Copy the generated API key',
        'Paste it in the API Keys section below',
      ],
      anthropic: [
        'Go to Anthropic website (https://console.anthropic.com)',
        'Sign in to your Anthropic account',
        'Navigate to API Keys section',
        'Click "Create Key"',
        'Copy the generated API key',
        'Paste it in the API Keys section below',
      ],
      google: [
        'Go to Google AI Studio (https://aistudio.google.com)',
        'Sign in to your Google account',
        'Navigate to API Keys section',
        'Click "Create API Key"',
        'Copy the generated API key',
        'Paste it in the API Keys section below',
      ],
    };

    return (
      steps[provider.toLowerCase()] || [
        `Go to ${provider} website`,
        'Sign in to your account',
        'Navigate to API Keys section',
        'Create a new API key',
        'Copy the generated API key',
        'Paste it in the API Keys section below',
      ]
    );
  };

  const getProviderWebsite = provider => {
    const websites = {
      openai: 'https://platform.openai.com',
      deepgram: 'https://deepgram.com',
      fireworks: 'https://fireworks.ai',
      elevenlabs: 'https://elevenlabs.io',
      anthropic: 'https://console.anthropic.com',
      google: 'https://aistudio.google.com',
    };

    return websites[provider.toLowerCase()] || `https://${provider.toLowerCase()}.com`;
  };

  const getProviderIcon = () => {
    // You can add specific icons for each provider here
    return <Key className="h-5 w-5" />;
  };

  const getProviderColor = provider => {
    const colors = {
      openai: 'bg-green-100 text-green-800 border-green-200',
      deepgram: 'bg-purple-100 text-purple-800 border-purple-200',
      fireworks: 'bg-orange-100 text-orange-800 border-orange-200',
      elevenlabs: 'bg-blue-100 text-blue-800 border-blue-200',
      anthropic: 'bg-red-100 text-red-800 border-red-200',
      google: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    };

    return colors[provider.toLowerCase()] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getProviderDarkColor = provider => {
    const colors = {
      openai: 'bg-green-900/30 text-green-300 border-green-800',
      deepgram: 'bg-purple-900/30 text-purple-300 border-purple-800',
      fireworks: 'bg-orange-900/30 text-orange-300 border-orange-800',
      elevenlabs: 'bg-blue-900/30 text-blue-300 border-blue-800',
      anthropic: 'bg-red-900/30 text-red-300 border-red-800',
      google: 'bg-yellow-900/30 text-yellow-300 border-yellow-800',
    };

    return colors[provider.toLowerCase()] || 'bg-gray-900/30 text-gray-300 border-gray-800';
  };

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose?.()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <AlertCircle className="h-6 w-6 text-red-500" />
            <span>API Key Configuration Required</span>
          </DialogTitle>
          <DialogDescription>
            You need to configure API keys for the following providers before proceeding. Follow the
            steps below for each provider.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {validationErrors.map((error, index) => {
            // Extract provider name from error message
            const providerMatch = error.match(/\(([^)]+)\)/);
            const provider = providerMatch ? providerMatch[1] : 'Unknown';
            const steps = getProviderSteps(provider);
            const website = getProviderWebsite(provider);

            return (
              <div
                key={index}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    {getProviderIcon(provider)}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white capitalize">
                        {provider} API Key
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Required for {provider} integration
                      </p>
                    </div>
                  </div>
                  <div
                    className={`px-3 py-1 rounded-full text-xs font-medium border ${getProviderColor(provider)} dark:${getProviderDarkColor(provider)}`}
                  >
                    <XCircle className="h-4 w-4 inline mr-1" />
                    Not Configured
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                      How to get your {provider} API key:
                    </h4>
                    <ol className="space-y-2">
                      {steps.map((step, stepIndex) => (
                        <li
                          key={stepIndex}
                          className="flex items-start space-x-3 text-sm text-gray-700 dark:text-gray-300"
                        >
                          <span className="flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full flex items-center justify-center text-xs font-medium">
                            {stepIndex + 1}
                          </span>
                          <span>{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>

                  <div className="flex items-center space-x-3">
                    <a
                      href={website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Go to {provider} Website
                    </a>
                    <Button
                      onClick={() => onGoToSettings(provider)}
                      variant="outline"
                      className="flex items-center space-x-2"
                    >
                      <Key className="h-4 w-4" />
                      <span>Configure API Key</span>
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              onGoToSettings();
              onClose();
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Key className="h-4 w-4 mr-2" />
            Go to API Keys Settings
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ApiKeyValidationModal;
